import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { Recipe, CookbookFilters, CookingHistoryEntry, ShoppingListItem, CookbookExport } from '@/types';
import { SCHEMA_VERSION } from '@/types';
import {
  getAllRecipes,
  saveRecipe,
  deleteRecipe as dbDeleteRecipe,
  getCookingHistory,
  addCookingHistoryEntry,
  clearCookingHistory,
  getShoppingList,
  saveShoppingListItem,
  deleteShoppingListItem,
  clearShoppingList,
  exportCookbook,
  importCookbook,
  type ImportResult,
  clearAllData as dbClearAllData,
} from '@/lib/data-layer';
import { generateId } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { getSeedRecipes } from '@/data/seed-recipes';
import { isFirebaseStorageUrl, deleteRecipeImage } from '@/lib/storage';

interface CookbookState {
  // Data
  recipes: Recipe[];
  cookingHistory: CookingHistoryEntry[];
  shoppingList: ShoppingListItem[];
  
  // UI State
  isLoading: boolean;
  error: string | null;
  filters: CookbookFilters;
  
  // Actions
  loadRecipes: () => Promise<void>;
  addRecipe: (recipe: Omit<Recipe, 'id' | 'createdAt' | 'updatedAt' | 'cookCount' | 'schemaVersion'>) => Promise<Recipe>;
  updateRecipe: (id: string, updates: Partial<Recipe>) => Promise<void>;
  deleteRecipe: (id: string) => Promise<void>;
  toggleFavorite: (id: string) => Promise<void>;
  recordCook: (recipeId: string, notes?: string, rating?: number) => Promise<void>;
  
  // History
  loadHistory: () => Promise<void>;
  clearHistory: () => Promise<void>;
  
  // Shopping List
  loadShoppingList: () => Promise<void>;
  addToShoppingList: (items: Omit<ShoppingListItem, 'id' | 'checked'>[]) => Promise<void>;
  toggleShoppingItem: (id: string) => Promise<void>;
  removeFromShoppingList: (id: string) => Promise<void>;
  clearShoppingListAction: () => Promise<void>;
  
  // Filters
  setFilters: (filters: Partial<CookbookFilters>) => void;
  resetFilters: () => void;
  
  // Seed data
  seedRecipes: () => Promise<void>;
  
  // Computed
  getFilteredRecipes: () => Recipe[];
  getRecipeById: (id: string) => Recipe | undefined;
  getFavorites: () => Recipe[];
  getRecentlyCooked: () => Recipe[];
  
  // Additional actions
  searchRecipes: (query: string) => Recipe[];
  exportRecipes: () => Promise<string>;
  importRecipes: (data: unknown) => Promise<ImportResult>;
  clearAllData: () => Promise<void>;
  incrementCookCount: (id: string) => Promise<void>;
}

const DEFAULT_FILTERS: CookbookFilters = {
  search: '',
  cuisine: 'all',
  difficulty: 'all',
  dietaryTags: [],
  favoritesOnly: false,
  sortBy: 'updatedAt',
  sortOrder: 'desc',
};

export const useCookbookStore = create<CookbookState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      recipes: [],
      cookingHistory: [],
      shoppingList: [],
      isLoading: false,
      error: null,
      filters: DEFAULT_FILTERS,
      
      // Load all recipes from IndexedDB
      loadRecipes: async () => {
        set({ isLoading: true, error: null });
        try {
          const recipes = await getAllRecipes();
          set({ recipes, isLoading: false });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load recipes',
            isLoading: false,
          });
        }
      },
      
      // Add a new recipe
      addRecipe: async (recipeData) => {
        const now = new Date().toISOString();
        const newRecipe: Recipe = {
          ...recipeData,
          id: generateId(),
          createdBy: recipeData.createdBy ?? useAuthStore.getState().user?.uid ?? undefined,
          isPublic: recipeData.isPublic ?? false,
          createdAt: now,
          updatedAt: now,
          cookCount: 0,
          schemaVersion: SCHEMA_VERSION,
        };
        
        try {
          await saveRecipe(newRecipe);
          // Guard: onSnapshot sync may have already added this recipe
          set((state) => {
            if (state.recipes.some((r) => r.id === newRecipe.id)) {
              return state;
            }
            return { recipes: [newRecipe, ...state.recipes] };
          });
          return newRecipe;
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to save recipe',
          });
          throw error;
        }
      },
      
      // Update an existing recipe
      updateRecipe: async (id, updates) => {
        const recipe = get().recipes.find((r) => r.id === id);
        if (!recipe) {
          throw new Error('Recipe not found');
        }
        
        const updatedRecipe: Recipe = {
          ...recipe,
          ...updates,
          updatedAt: new Date().toISOString(),
        };
        
        try {
          await saveRecipe(updatedRecipe);
          set((state) => ({
            recipes: state.recipes.map((r) =>
              r.id === id ? updatedRecipe : r
            ),
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update recipe',
          });
          throw error;
        }
      },
      
      // Delete a recipe
      deleteRecipe: async (id) => {
        try {
          // Best-effort cleanup of Firebase Storage image
          const recipe = get().recipes.find((r) => r.id === id);
          if (recipe && isFirebaseStorageUrl(recipe.imageUrl)) {
            try {
              await deleteRecipeImage(id);
            } catch {
              // Image cleanup is best-effort — recipe deletion is more important
            }
          }

          await dbDeleteRecipe(id);
          set((state) => ({
            recipes: state.recipes.filter((r) => r.id !== id),
          }));
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete recipe',
          });
          throw error;
        }
      },
      
      // Toggle favorite status
      toggleFavorite: async (id) => {
        const recipe = get().recipes.find((r) => r.id === id);
        if (recipe) {
          await get().updateRecipe(id, { isFavorite: !recipe.isFavorite });
        }
      },
      
      // Record that a recipe was cooked
      recordCook: async (recipeId, notes, rating) => {
        const recipe = get().recipes.find((r) => r.id === recipeId);
        if (!recipe) return;
        
        // Update recipe stats
        await get().updateRecipe(recipeId, {
          cookCount: recipe.cookCount + 1,
          lastCookedAt: new Date().toISOString(),
        });
        
        // Add to cooking history
        const entry: CookingHistoryEntry = {
          id: generateId(),
          recipeId,
          recipeName: recipe.name,
          cookedAt: new Date().toISOString(),
          notes,
          rating,
        };
        
        await addCookingHistoryEntry(entry);
        set((state) => ({
          cookingHistory: [entry, ...state.cookingHistory],
        }));
      },
      
      // Load cooking history
      loadHistory: async () => {
        try {
          const history = await getCookingHistory();
          set({ cookingHistory: history });
        } catch (error) {
          console.error('Failed to load cooking history:', error);
        }
      },
      
      // Clear cooking history
      clearHistory: async () => {
        try {
          await clearCookingHistory();
          set({ cookingHistory: [] });
        } catch (error) {
          console.error('Failed to clear cooking history:', error);
        }
      },
      
      // Load shopping list
      loadShoppingList: async () => {
        try {
          const list = await getShoppingList();
          set({ shoppingList: list });
        } catch (error) {
          console.error('Failed to load shopping list:', error);
        }
      },
      
      // Add items to shopping list
      addToShoppingList: async (items) => {
        const newItems: ShoppingListItem[] = items.map((item) => ({
          ...item,
          id: generateId(),
          checked: false,
        }));
        
        for (const item of newItems) {
          await saveShoppingListItem(item);
        }
        
        set((state) => ({
          shoppingList: [...state.shoppingList, ...newItems],
        }));
      },
      
      // Toggle shopping list item
      toggleShoppingItem: async (id) => {
        const item = get().shoppingList.find((i) => i.id === id);
        if (item) {
          const updated = { ...item, checked: !item.checked };
          await saveShoppingListItem(updated);
          set((state) => ({
            shoppingList: state.shoppingList.map((i) =>
              i.id === id ? updated : i
            ),
          }));
        }
      },
      
      // Remove from shopping list
      removeFromShoppingList: async (id) => {
        await deleteShoppingListItem(id);
        set((state) => ({
          shoppingList: state.shoppingList.filter((i) => i.id !== id),
        }));
      },
      
      // Clear shopping list
      clearShoppingListAction: async () => {
        await clearShoppingList();
        set({ shoppingList: [] });
      },
      
      // Set filters
      setFilters: (filters) => {
        set((state) => ({
          filters: { ...state.filters, ...filters },
        }));
      },
      
      // Reset filters
      resetFilters: () => {
        set({ filters: DEFAULT_FILTERS });
      },
      
      // Seed with sample recipes (skips duplicates by name)
      seedRecipes: async () => {
        const existingNames = new Set(get().recipes.map((r) => r.name.toLowerCase()));
        const seedData = getSeedRecipes().filter(
          (r) => !existingNames.has(r.name.toLowerCase())
        );
        if (seedData.length === 0) return;
        for (const recipe of seedData) {
          await saveRecipe(recipe);
        }
        set((state) => ({
          recipes: [...seedData, ...state.recipes],
        }));
      },
      
      // Get filtered recipes
      getFilteredRecipes: () => {
        const { recipes, filters } = get();
        
        let filtered = [...recipes];
        
        // Search
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filtered = filtered.filter(
            (r) =>
              r.name.toLowerCase().includes(searchLower) ||
              r.description.toLowerCase().includes(searchLower) ||
              r.tags.some((t) => t.toLowerCase().includes(searchLower)) ||
              r.ingredients.some((i) => i.name.toLowerCase().includes(searchLower))
          );
        }
        
        // Cuisine
        if (filters.cuisine !== 'all') {
          filtered = filtered.filter((r) => r.cuisine === filters.cuisine);
        }
        
        // Difficulty
        if (filters.difficulty !== 'all') {
          filtered = filtered.filter((r) => r.difficulty === filters.difficulty);
        }
        
        // Dietary tags
        if (filters.dietaryTags.length > 0) {
          filtered = filtered.filter((r) =>
            filters.dietaryTags.every((tag) => r.dietaryTags.includes(tag))
          );
        }
        
        // Favorites only
        if (filters.favoritesOnly) {
          filtered = filtered.filter((r) => r.isFavorite);
        }
        
        // Sort
        filtered.sort((a, b) => {
          let aVal: string | number;
          let bVal: string | number;
          
          switch (filters.sortBy) {
            case 'name':
              aVal = a.name.toLowerCase();
              bVal = b.name.toLowerCase();
              break;
            case 'createdAt':
              aVal = new Date(a.createdAt).getTime();
              bVal = new Date(b.createdAt).getTime();
              break;
            case 'updatedAt':
              aVal = new Date(a.updatedAt).getTime();
              bVal = new Date(b.updatedAt).getTime();
              break;
            case 'lastCookedAt':
              aVal = a.lastCookedAt ? new Date(a.lastCookedAt).getTime() : 0;
              bVal = b.lastCookedAt ? new Date(b.lastCookedAt).getTime() : 0;
              break;
            case 'cookCount':
              aVal = a.cookCount;
              bVal = b.cookCount;
              break;
            default:
              aVal = 0;
              bVal = 0;
          }
          
          if (filters.sortOrder === 'asc') {
            return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          } else {
            return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
          }
        });
        
        return filtered;
      },
      
      // Get recipe by ID
      getRecipeById: (id) => {
        return get().recipes.find((r) => r.id === id);
      },
      
      // Get favorites
      getFavorites: () => {
        return get().recipes.filter((r) => r.isFavorite);
      },
      
      // Get recently cooked
      getRecentlyCooked: () => {
        return get()
          .recipes.filter((r) => r.lastCookedAt)
          .sort((a, b) => {
            const aTime = a.lastCookedAt ? new Date(a.lastCookedAt).getTime() : 0;
            const bTime = b.lastCookedAt ? new Date(b.lastCookedAt).getTime() : 0;
            return bTime - aTime;
          })
          .slice(0, 10);
      },
      
      // Search recipes
      searchRecipes: (query: string) => {
        const { recipes } = get();
        const searchLower = query.toLowerCase();
        return recipes.filter(
          (r) =>
            r.name.toLowerCase().includes(searchLower) ||
            r.description.toLowerCase().includes(searchLower) ||
            r.tags.some((t) => t.toLowerCase().includes(searchLower))
        );
      },
      
      // Export a full cookbook backup (recipes, history, shopping, pantry, meal plans,
      // settings) as a pretty-printed JSON string.
      exportRecipes: async () => {
        const data = await exportCookbook();
        return JSON.stringify(data, null, 2);
      },

      // Import a cookbook backup. Accepts anything that looks like a CookbookExport —
      // Zod schemas inside data-layer.importCookbook filter out invalid entries rather
      // than throwing. Reloads recipes/history/shopping locally; pantry and meal plans
      // are refreshed via their own stores' sync listeners when authenticated.
      importRecipes: async (data: unknown) => {
        if (!data || typeof data !== 'object') {
          throw new Error('Invalid import data');
        }

        // Accept legacy backups that only contained `{ recipes: [...] }` by wrapping
        // them in a minimal CookbookExport shape before validation.
        const backup = data as Partial<CookbookExport>;
        if (!backup.schemaVersion && Array.isArray(backup.recipes)) {
          (backup as CookbookExport).schemaVersion = 1;
          (backup as CookbookExport).exportedAt = new Date().toISOString();
        }

        const result = await importCookbook(backup as CookbookExport);

        // Refresh the bits this store owns. Pantry and meal plans live in other stores
        // and will either receive Firestore snapshot updates (authenticated) or can be
        // reloaded by the page that consumes them.
        await Promise.all([
          get().loadRecipes(),
          get().loadHistory(),
          get().loadShoppingList(),
        ]);

        return result;
      },
      
      // Clear all data
      clearAllData: async () => {
        await dbClearAllData();
        set({ recipes: [], cookingHistory: [], shoppingList: [] });
      },
      
      // Increment cook count
      incrementCookCount: async (id: string) => {
        const recipe = get().recipes.find((r) => r.id === id);
        if (recipe) {
          await get().updateRecipe(id, {
            cookCount: recipe.cookCount + 1,
            lastCookedAt: new Date().toISOString(),
          });
        }
      },
    })),
    { name: 'cookbook-store' }
  )
);
